import { colors, Grid, VStack } from "@biblioteksentralen/react";
import { useEffect, useState } from "react";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { DefaultContainer } from "../ContentContainer";
import { usePreviewContext } from "../preview/previewContext";
import AdventCalendarBanner from "./AdventCalendarBanner";
import { CalendarDoorModal } from "./CalendarDoorModal/CalendarDoorModal";
import { CalendarDoorNumber } from "./ChristmasPresent/CalendarDoorNumber";
import { ChristmasPresent, christmasPresentColors } from "./ChristmasPresent/ChristmasPresent";
import { adventDates } from "./utils";
import { ResolvedAdventCalendar } from "@libry-content/common";
import { isPromotedNow } from "@libry-content/common/src/promotion/utils";

/**
 * We want to set the current date after the second render cycle:
 * - after static build happens SSR not to be stuck on the build date
 * - after the first so that SSR build and first client render are identical
 * - after the second so that the CSS transition works
 */
const useActiveDates = () => {
  const [dates, setDates] = useState(0);
  const [secondRenderFinished, setSecondRenderFinished] = useState(false);

  useEffect(() => {
    if (!secondRenderFinished) setSecondRenderFinished(true);
    else setDates(Number(new Date().getDate()));
  }, [secondRenderFinished]);

  return dates;
};

const presentColors = [christmasPresentColors[1], christmasPresentColors[2], christmasPresentColors[4]];

const AdventCalendarView = ({ adventCalendar }: { adventCalendar: ResolvedAdventCalendar }) => {
  const { t } = useTranslation();
  const activeDates = Math.max(
    Math.min(useActiveDates(), adventCalendar.doors?.length ?? 0),
    isPromotedNow(adventCalendar.promotion) ? 0 : 24
  );
  const { isOn: previewIsOn } = usePreviewContext();

  const [calendarDoorModalDate, setCalendarDoorModalDate] = useState<number | undefined>(undefined);
  const calendarDoorModalIsOpen = typeof calendarDoorModalDate !== "undefined";

  return (
    <DefaultContainer
      margin={{ base: "0 0 3rem", sm: "1.5rem auto 5rem", md: "2rem auto 8rem" }}
      padding="0"
      position="relative"
    >
      <VStack
        margin="auto"
        gap={{ base: "1rem", sm: "2rem", md: "4rem", lg: "5rem" }}
        padding={{ base: "0", sm: "0 1.5rem", md: "0 2rem" }}
      >
        <AdventCalendarBanner
          adventCalendar={adventCalendar}
          text={t("Finn dagens luke og se hva som skjuler seg bak.")}
          title={t("Julekalender {år}", { år: new Date().getFullYear() })}
          hidePresentsBelow="sm"
          presentColors={presentColors}
        />
        <Grid
          gridTemplateColumns={{
            base: "repeat(auto-fill, minmax(5.5rem, 1fr))",
            sm: "repeat(auto-fill, minmax(6.5rem, 1fr))",
            md: "repeat(auto-fill, minmax(7.3rem, 1fr))",
            lg: "repeat(auto-fill, minmax(8.5rem, 1fr))",
          }}
          justifyItems="center"
          columnGap="0"
          rowGap={{ base: "3.2rem", lg: "3.5rem" }}
          paddingTop="2rem"
          width="100%"
        >
          {adventDates.map(({ date, colorIndex }) => {
            const isActive = previewIsOn || date <= activeDates;
            const transitionDelay = `${date / 10}s`;
            const dateNumberColor = isActive ? colors.black : colors.grey30;
            const presentColors = isActive ? christmasPresentColors[colorIndex] : christmasPresentColors[0];

            const onSelect = isActive ? () => setCalendarDoorModalDate(date) : undefined;

            return (
              <ChristmasPresent
                key={date}
                width={{ base: "5rem", sm: "5.8rem", md: "6.5rem", lg: "7.5rem" }}
                height={{ base: "4.5rem", sm: "5.22rem", md: "5.85rem", lg: "6.75rem" }} // 90% of width
                position="relative"
                transitionDelay={transitionDelay}
                animateOnHover={isActive}
                onClick={onSelect}
                {...presentColors}
                tabIndex={isActive ? 0 : -1}
                as="button"
                aria-label={t("Kalenderluke for {dato}. desember", { dato: date })}
              >
                <CalendarDoorNumber
                  date={date}
                  color={dateNumberColor}
                  width={{ base: "2.1rem", sm: "2.6rem", md: "2.8rem", lg: "3rem" }}
                  height={{ base: "2.1rem", sm: "2.6rem", md: "2.8rem", lg: "3rem" }}
                  fontSize={{ base: "2xl", md: "3xl" }}
                  transitionDelay={transitionDelay}
                />
              </ChristmasPresent>
            );
          })}
        </Grid>
      </VStack>
      {calendarDoorModalIsOpen && adventCalendar.doors?.[calendarDoorModalDate - 1] && (
        <CalendarDoorModal
          date={calendarDoorModalDate}
          onClose={() => setCalendarDoorModalDate(undefined)}
          door={adventCalendar.doors[calendarDoorModalDate - 1]!}
        />
      )}
    </DefaultContainer>
  );
};

export default AdventCalendarView;
